import { EventDispatcher, documentReady, gebcn, gebi, isMobileView, isValidEmail, l, lc, lerp, qs, qsa } from '@utils/toolbox';
import { ConnectApi } from '@js/plugins/connectApi';
import { SVGSpinner } from '@js/components/spinner';
import { SearchBox } from '@module/SearchBox/SearchBox';
import noUiSlider from '@js/plugins/nouislider';


const carbonCompensate = {
	init () {
		this.$dom = {
			homeIntro: gebi('home-intro-sections'),
			searchBox: gebi('abSearchBox'),
			decreasePassengers: gebi('decrease-passengers'),
			increasePassengers: gebi('increase-passengers'),
			passengersCount: gebi('passenger-count'),
			flightTypeCheckbox: gebi('flightType'),
			reduceSlider: gebi('slider-reduce'),
			reducePrice: gebi('reduce-price'),
			reduceText: gebi('reduce-text'),
			reduceLink: gebi('reduce-link'),
			reduceEmissions: gebi('reduce-emissions')
		};

		this.store = {
			co2: null,
			co2Price: null
		};

		this.initSearchBox();
		this.initSlider();
		this.bindEvents();
	},
	initSlider () {
		noUiSlider.create(this.$dom.reduceSlider,
			{
				pips: {
					mode: 'count',
					values: 3,
					density: 4
				},
				format: {
					to: v => v | 0,
					from: v => v | 0
				},
				step: 1,
				tooltips: [true],
				start: [js_params.climateCompensationBiofuelStartPercentage],
				connect: [true, false],
				range: {
					min: 0,
					max: 100
				}
			});
		// Add a percent sign to the tooltips
		this.$dom.reduceSlider.noUiSlider.on('update', (values, handle) => {
			this.$dom.reduceSlider.querySelector('.noUi-tooltip').innerHTML = `${values[handle]}%`;
			this.$dom.reduceText.innerHTML = l('searchBox.climateComp.reduce') + ' ' + values[handle] + '%';
			if (!this.store.co2) {
				return;
			}
			this.setBiofuelCost();
		});
	},

	setBiofuelCost () {
		const sliderPercentage = this.$dom.reduceSlider.noUiSlider.get() / 100;
		const price = lerp(0, this.store.co2 * js_params.climateCompensationBiofuelCostPerKg, sliderPercentage);
		const url = new URL(this.$dom.reduceLink.href);
		url.searchParams.set('value', price.toFixed());
		this.$dom.reduceLink.href = url.toString();
		this.$dom.reducePrice.innerHTML = lc(price);

		this.$dom.reduceEmissions.innerHTML = lerp(0, this.store.co2, sliderPercentage).toFixed().toLocaleString('sv') + ' kg CO₂';
	},

	initSearchBox () {
		window.searchBox = new SearchBox({
			id: 'abSearchBox',
			storeInLocalStorage: false,
			calendarRender: gebi('ab-dateselector-carbon'),
			config: { tripType: 2 },
			isCarbon: true
		});
	},

	listItemClick (listItem) {
		const parent = listItem.closest('.another-emission-parent');
		const listItemParent = listItem.closest('.list-wrapper');
		const input = qs('input', parent);
		this.toggleInputFocus({ target: input }, false);
		if (!listItem?.dataset?.iata) {
			return;
		}
		input.value = listItem.innerHTML;
		input.dataset.iata = listItem.dataset.iata;
		listItemParent.innerHTML = '';
		parent.classList.add('no-autocomplete');
		this.callEmissionCalc();
	},

	toggleInputFocus (event, isFocus) {
		const eventTarget = event.target.closest('.another-emission-parent');
		const className = 'another-emission-parent--active';
		const classNameParent = 'abSearchBox--show-overlay';
		const method = isFocus ? 'add' : 'remove';
		eventTarget.classList[method](className);
		this.$dom.searchBox.classList[method](classNameParent);
		if (isFocus) {
			this.initialScrollPos = window.scrollY;
			const elemOffset = event.target.getBoundingClientRect().top + window.scrollY - 40;

			if (isMobileView()) {
				window.scrollTo({ top: elemOffset });
			}
		} else {
			window.scrollTo(this.initialScrollPos || 0, 0);
		}
	},

	bindEvents () {
		this.$dom.searchBox.addEventListener('keydown', (event) => {
			if (event.target.classList.contains('emission-airline-input') || event.target.classList.contains('emission-aircraft-input')) {
				switch (event.key) {
					case 'Enter': {
						const listItem = event.target.closest('.another-emission-parent').querySelector('.list-item--hover');
						this.listItemClick(listItem);
						event.preventDefault();
						EventDispatcher.dispatch('targetNextInput', event.target);
						break;
					}
					case 'ArrowUp':
					case 'ArrowDown': {
						const list = event.target.closest('.another-emission-parent').querySelector('.list-wrapper');
						const rows = qsa('.list-item', list);
						const hoverClass = 'list-item--hover';
						let hoveredRow = list.querySelector(`.${hoverClass}`);

						if (!rows.length) {
							return;
						}

						if (!hoveredRow) {
							hoveredRow = rows[0];
							hoveredRow.classList.add(hoverClass);
							return;
						}

						const hoveredIndex = rows.indexOf(hoveredRow);

						// Determine target row index
						const newIndex = (event.key === 'ArrowUp') ? Math.max(0, hoveredIndex - 1) : Math.min(rows.length - 1, hoveredIndex + 1);

						// Update hovered row
						hoveredRow.classList.remove(hoverClass);
						rows[newIndex].classList.add(hoverClass);

						break;
					}
				}
			}
		});

		this.$dom.searchBox.addEventListener('focusout', (event) => {
			const eventTarget = event.target;
			// Flight number
			if (eventTarget.classList.contains('flight-number-input')) {
				this.toggleInputFocus(event, false);
				const inputValue = eventTarget.value;
				if (!inputValue) {
					eventTarget.removeAttribute('data-iata');
					return;
				}
				const leg = eventTarget.closest('.abSearchBox__leg');
				if (inputValue.length > 3) {
					this.callGetFlightNr(inputValue, leg);
				}
			}

			// Aircraft / Airline
			if (eventTarget.classList.contains('emission-aircraft-input') || eventTarget.classList.contains('emission-airline-input')) {
				eventTarget.closest('.another-emission-parent').classList.add('no-autocomplete');
				const listElement = qs('.list-wrapper', event.target.closest('.another-emission-parent'));
				if (!eventTarget?.dataset?.iata) {
					eventTarget.value = '';
				} else if (!eventTarget.value) {
					eventTarget.removeAttribute('data-iata');
				}
				this.toggleInputFocus(event, false);
				listElement.innerHTML = '';
				this.callEmissionCalc();
			}
		});

		this.$dom.searchBox.addEventListener('focusin', (event) => {
			// Aircraft / Airline / Flight number
			if (event.target.classList.contains('emission-input')) {
				this.toggleInputFocus(event, true);
			}
		});

		this.$dom.searchBox.addEventListener('input', (event) => {
			const eventTarget = event.target;
			// Airline and aircraft inputs
			if (event.target.classList.contains('emission-aircraft-input') || event.target.classList.contains('emission-airline-input')) {
				// Controversial "feature": dirty field, reset value
				eventTarget.removeAttribute('data-iata');
				const configs = {
					'emission-airline-input': {
						index: 0,
						headerText: l('carbonCompensate.input.airline.header'),
						searchAircraftAirline: 'searchAirlines',
						connectApiResponseProperty: 'code',
						listElement: qs('.list-wrapper', eventTarget.closest('.another-emission-parent'))
					},
					'emission-aircraft-input': {
						index: 1,
						headerText: l('carbonCompensate.input.aircraft.header'),
						searchAircraftAirline: 'searchAircrafts',
						connectApiResponseProperty: 'iata',
						listElement: qs('.list-wrapper', eventTarget.closest('.another-emission-parent'))
					}
				};

				const aircraftOrAirline = event.target.classList.contains('emission-aircraft-input') ? 'emission-aircraft-input' : 'emission-airline-input';
				const config = configs[aircraftOrAirline];

				ConnectApi('EmissionCalc', config.searchAircraftAirline, [event.target.value]).then((response) => {
					eventTarget.closest('.another-emission-parent').classList.remove('no-autocomplete');
					const listElement = qs('.list-wrapper', event.target.closest('.another-emission-parent'));
					let listHtml = '';

					const generateText = item => item.name.includes(`(${item[config.connectApiResponseProperty]})`) ? item.name : `${item.name} (${item[config.connectApiResponseProperty]})`;
					const generateLiElement = item => `<li class="list-item" data-iata="${item[config.connectApiResponseProperty]}">${generateText(item)}</li>`;
					const ulHeader = `<li class="list-item-header SearchFieldMulti__section_header SearchFieldMulti__section_header--rounded">${config.headerText}</li>`;
					const items = response.slice(0, 10);
					items.forEach((item) => {
						listHtml += generateLiElement(item);
					});
					if (items.length === 0) {
						listHtml = `<li class="list-item">${l('showresult.noMoreResults')}</li>`;
					}
					listElement.innerHTML = ulHeader + listHtml;
				});
			}
		});

		this.$dom.searchBox.addEventListener('mousedown', (event) => {
			const eventTarget = event.target;

			if (eventTarget.classList.contains('abSearchBox__overlay')) {
				this.$dom.searchBox.classList.remove('abSearchBox--show-overlay');
				qsa('.emission-input', this.$dom.searchBox).forEach(input => this.toggleInputFocus({ target: input }, false));
				this.callEmissionCalc();
			}

			// Decrease passengers
			if (eventTarget === this.$dom.decreasePassengers) {
				const numberOfPassengers = parseInt(this.$dom.passengersCount.innerText) - 1;
				if (numberOfPassengers > 0) {
					this.$dom.passengersCount.innerText = numberOfPassengers;
					this.callEmissionCalc();
				}
				if (numberOfPassengers < 2) {
					this.$dom.decreasePassengers.classList.add('abPassengers__btn--disabled');
				}

				this.callEmissionCalc();
				return;
			}

			// Increase passengers
			if (eventTarget === this.$dom.increasePassengers) {
				const numberOfPassengers = parseInt(this.$dom.passengersCount.innerText) + 1;
				this.$dom.passengersCount.innerText = numberOfPassengers;
				this.$dom.decreasePassengers.classList.remove('abPassengers__btn--disabled');
				this.callEmissionCalc();
			}

			// List item
			if (eventTarget.matches('.list-item')) {
				this.listItemClick(eventTarget);
			}
		});

		this.$dom.searchBox.addEventListener('click', (event) => {
			// Focus child input on parent click
			if (event.target.closest('.emission-relative-parent')) {
				const parent = event.target.closest('.emission-relative-parent');
				qs('input', parent)?.focus();
			}

			if (event.target.classList.contains('SearchFieldMulti__apply_btn')) {
				this.callEmissionCalc();
			}
		});

		this.$dom.flightTypeCheckbox.addEventListener('change', () => {
			this.callEmissionCalc();
		});

		EventDispatcher.addEventListener('searchBox:legAdded', () => {
			const fromInputs = gebcn('SearchFieldMulti__field--from');
			fromInputs[fromInputs.length - 1].dataset.iata = '';
			fromInputs[fromInputs.length - 1].value = '';
			const toInputs = gebcn('SearchFieldMulti__field--to');
			toInputs[toInputs.length - 1].dataset.iata = '';
			toInputs[toInputs.length - 1].value = '';
			const airlineInputs = gebcn('emission-airline-input');
			airlineInputs[airlineInputs.length - 1].dataset.iata = '';
			airlineInputs[airlineInputs.length - 1].value = '';
			const aircraftInputs = gebcn('emission-aircraft-input');
			aircraftInputs[aircraftInputs.length - 1].dataset.iata = '';
			aircraftInputs[aircraftInputs.length - 1].value = '';
			const flightNumberInputs = gebcn('flight-number-input');
			flightNumberInputs[flightNumberInputs.length - 1].value = '';
			this.fixInputTabIndexes();
		});

		EventDispatcher.addEventListener('searchBox:legRemoved', () => {
			this.callEmissionCalc();
		});

		const searchFieldMultiChangedHandler = ({ inputElement }) => {
			if (!qs('.SearchFieldMulti__checked_iatas_container', inputElement.parentElement).innerText) {
				inputElement.dataset.iata = '';
			}

			// because we hide aircraft and airline input boxes on mobile they need to be reset
			if (isMobileView()) {
				const leg = inputElement.closest('.abSearchBox__leg');
				// Aircraft
				qs('.emission-aircraft-input', leg).dataset.iata = '';
				qs('.emission-aircraft-input', leg).value = '';

				// Airline
				qs('.emission-airline-input', leg).dataset.iata = '';
				qs('.emission-airline-input', leg).value = '';
			}

			this.callEmissionCalc();
		};

		EventDispatcher.addEventListener('onFROMIatasChanged', searchFieldMultiChangedHandler.bind(this));
		EventDispatcher.addEventListener('onTOIatasChanged', searchFieldMultiChangedHandler.bind(this));

		// Event handlers on element only existing on Flygresor.se (Om oss section and newsletter)
		if (js_params.domain === 'flygresor.se') {
			const newsForm = gebi('news-letter-form');

			gebi('home-info-show-more-btn').addEventListener('click', (e) => {
				const hiddenSections = this.$dom.homeIntro.getElementsByClassName('home-intro__section--hidden');

				Array.prototype.forEach.call(hiddenSections, (section) => {
					section.classList.remove('home-intro__section--hidden');
				});

				e.target.parentNode.style.display = 'none';
			});


			// About handling events for the newsletter subscription
			newsForm.addEventListener('submit', (e) => {
				e.preventDefault();

				const emailField = e.target.querySelector('#email-field');
				const emailAddress = emailField.value;

				if (isValidEmail(emailAddress)) {
					ConnectApi('NewsletterSubscription', 'subscribe', [emailAddress]).then((response) => {
						window.trackEvent.triggerGAevent('newsletter', 'registration');
						newsForm.innerHTML = response;
					});
				} else {
					window.alert(l('pricewatcher.form.emailError'));
					e.target.querySelector('.input-field-section').classList.add('error');
					emailField.focus();
				}
			});
		}
	},
	callEmissionCalc () {
		// Loop over all legs and get data from child elements, then remove any legs without a "from" or "to"
		const allMulti = qsa('.abSearchBox__leg')
			.map(leg => ({
				from: qs('.SearchFieldMulti__field--from', leg).getAttribute('data-iata') || '',
				to: qs('.SearchFieldMulti__field--to', leg).getAttribute('data-iata') || '',
				aircraft: qs('.emission-aircraft-input', leg).getAttribute('data-iata') || '',
				airline: qs('.emission-airline-input', leg).getAttribute('data-iata') || '',
				numPassengers: qs('#passenger-count').innerHTML
			}))
			.filter(leg => (leg.from && leg.to) && (leg.from !== leg.to));

		const isReturn = gebi('flightType').checked;

		if (allMulti.length) {
			ConnectApi('EmissionCalc', 'calculateFlightInfo', [allMulti, isReturn]).then((response) => {
				response = JSON.parse(response);
				this.store.co2 = Math.round(response[0]);
				this.store.co2Price = Math.round(0.150019357 * response[0]);
				gebi('actual-estimate').innerHTML = this.store.co2.toLocaleString('sv') + ' kg CO₂';
				gebi('emission-price').innerHTML = lc(this.store.co2Price);
				gebi('emission-link').href = response[1];
				gebi('emission-link').target = '_blank';
				this.setBiofuelCost();
			});
		} else {
			this.store.co2 = null;
			this.store.co2Price = null;
			gebi('actual-estimate').innerHTML = '___ kg CO₂';
			gebi('reduce-emissions').innerHTML = '___ kg CO₂';
			gebi('emission-price').innerHTML = '___ kr';
			gebi('reduce-price').innerHTML = '___ kr';
			gebi('emission-link').href = '';
		}
	},
	showLoadingSpinner (leg, show = true) {
		if (show) {
			const target = qs('.flight-number', leg).parentElement;
			if (qs('.svg-spinner', target)) {
				return;
			}
			const spinner = new SVGSpinner({ target: target, className: 'svg-spinner' });
			spinner.show();
		} else {
			qs('.svg-spinner', leg)?.remove();
		}
	},
	callGetFlightNr (inputValue, leg) {
		this.showLoadingSpinner(leg, true);
		ConnectApi('EmissionCalc', 'getFlightNumbers', [inputValue]).then(([response] = [{ aircraft_name: [], airline_name: [], dep_name: null, dest_name: null }]) => {
			// with no response iata we can't give any meaningful results
			if (response.error || !response?.dep?.iata || !response) {
				throw response.error;
			}

			// Aircraft
			qs('.emission-aircraft-input', leg).dataset.iata = response.aircraft.iata;
			qs('.emission-aircraft-input', leg).value = response.aircraft.name;

			// Airline
			qs('.emission-airline-input', leg).dataset.iata = response.airline.iata;
			qs('.emission-airline-input', leg).value = response.airline.name;

			// From leg
			const fromElem = qs('.SearchFieldMulti__field--from', leg);
			fromElem.dataset.iata = response.dep.iata;
			fromElem.value = response.dep.name;
			qs('.SearchFieldMulti__checked_iatas_container', fromElem.parentElement).innerHTML = '';

			// To leg
			const toElem = qs('.SearchFieldMulti__field--to', leg);
			toElem.dataset.iata = response.dest.iata;
			toElem.value = response.dest.name;
			qs('.SearchFieldMulti__checked_iatas_container', toElem.parentElement).innerHTML = '';

			this.callEmissionCalc();
		}).catch((error) => {
			alert(l(error));
		}).finally(() => {
			this.showLoadingSpinner(leg, false);
		});
	},
	fixInputTabIndexes () {
		qsa('input').forEach((input, index) => {
			input.tabIndex = index + 1;
		});
	}
};

documentReady(() => {
	carbonCompensate.init();
});

window.carbonCompensate = carbonCompensate;
